/**
 * Bảng "ai đang truy cập tên miền này" — FR-076.
 *
 * Dùng ở hai nơi:
 *   - `permissions.html` → tab *Theo tên miền* (data-od-id="perm-domain-table")
 *   - `domain-detail.html` → tab *Quyền truy cập* (data-od-id="access-table")
 *
 * `DomainAccessPanel` là cả tabpanel của `domain-detail.html`, XUẤT ra để mốc
 * M4 (Agent A) import — M5 không sửa trang của mốc khác.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { avatarTone, useFormat } from '../../lib/format';
import { AsyncSection, SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';
import { GrantLevelSelect, RelationChip } from './GrantLevelSelect';
import { permApi, permKeys, type GrantView, type PendingGrantView } from './api';

export function DomainAccessTable({
  domainId,
  chiDoc,
  domainName,
  grants,
  pending,
  odId,
  onRevokeInvite,
  onResendInvite,
}: {
  domainId: string;
  /** OQ-243 — chỉ hiển thị, mọi thao tác phân quyền làm ở trang Phân quyền tên miền. */
  chiDoc?: boolean;
  domainName: string;
  grants: GrantView[];
  pending: PendingGrantView[];
  odId: string;
  /** Chọn mức "Chủ sở hữu" ở một dòng → mở luồng chuyển quyền sở hữu. */
  onRevokeInvite?: (pendingId: string) => void;
  /** OQ-244 — gửi lại lời mời: gọi lại chính POST /invitations (ON CONFLICT DO UPDATE). */
  onResendInvite?: (invite: PendingGrantView) => void;
}) {
  const { formatDateTime } = useFormat();
  const t = useT();
  const { can } = useAuth();
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();

  /*
   * OQ-243 — `chiDoc` khoá mọi thao tác.
   *
   * Tab "Quyền truy cập" ở trang chi tiết tên miền chỉ hiển thị; mọi thao tác
   * phân quyền làm ở trang Phân quyền tên miền. Khoá ở một biến duy nhất thay
   * vì rắc điều kiện khắp bảng: bỏ sót một chỗ là mở lại đúng lỗ vừa bịt.
   *
   * Vế thứ hai xét CẢ HAI TẦNG như backend. `can('share.grant')` một mình là
   * quyền HỆ THỐNG, mà mọi Thành viên đều có (họ cần nó cho tên miền của chính
   * mình) — nên người chỉ được cấp "Biên tập DNS" trên tên miền này vẫn thấy ô
   * đổi mức quyền, kể cả mức của chính họ.
   */
  const canShare = !chiDoc && can('share.grant');
  const canRevoke = !chiDoc && can('share.revoke');

  const rolesQuery = useQuery({
    queryKey: permKeys.domainRoles,
    queryFn: permApi.domainRoles,
    staleTime: 10 * 60 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: permKeys.all });
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const revoke = useMutation({
    mutationFn: (grant: GrantView) => permApi.revokeGrant(domainId, grant.id!),
    onSuccess: async (_data, grant) => {
      await invalidate();
      toast(t('perm.revoked', { name: grant.user.fullName, domain: domainName }));
    },
    onError,
  });

  return (
    <div className="table-wrap">
      <table className="tbl" data-od-id={odId}>
        <thead>
          <tr>
            <th scope="col">{t('perm.col.user')}</th>
            <th scope="col">{t('perm.col.relation')}</th>
            <th scope="col">{t('perm.col.level')}</th>
            <th scope="col">{t('perm.col.grantedAt')}</th>
            {/* OQ-243 — tab chỉ đọc thì không có gì để thao tác, bỏ luôn cột. */}
            {chiDoc ? null : (
              <th scope="col" className="right">
                {t('perm.col.actions')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {grants.map((grant) => (
            <tr key={grant.id ?? grant.user.id}>
              <td>
                <div className="row" style={{ gap: 9, flexWrap: 'nowrap' }}>
                  <span className={`avatar sm ${avatarTone(grant.user.email)}`}>
                    {grant.user.initials}
                  </span>
                  <span>
                    <b>{grant.user.fullName}</b>
                    <div className="sub">{grant.user.email}</div>
                  </span>
                </div>
              </td>
              <td>
                <RelationChip relation={grant.relation} />
              </td>
              <td>
                {grant.revocable && grant.domainRole && canShare ? (
                  <GrantLevelSelect
                    value={grant.domainRole.id}
                    roles={rolesQuery.data ?? []}
                    ariaLabel={`${t('perm.col.level')} ${grant.user.fullName}`}
                    onError={onError}
                    onChange={async (domainRoleId) => {
                      const updated = await permApi.updateGrant(domainId, grant.id!, domainRoleId);
                      await invalidate();
                      toast(
                        t('perm.levelChanged', {
                          name: grant.user.fullName,
                          domain: domainName,
                          level: updated.domainRole?.name ?? '',
                        }),
                      );
                    }}
                  />
                ) : (
                  <span className="small muted">
                    {grant.isOwner ? t('perm.ownerLevel') : (grant.domainRole?.name ?? '—')}
                  </span>
                )}
              </td>
              <td className="small muted">
                {grant.createdAt ? formatDateTime(grant.createdAt) : t('perm.grantedWhenAdded')}
                {grant.grantedBy ? (
                  <div className="sub">
                    {t('perm.grantedBy', { name: grant.grantedBy.fullName })}
                  </div>
                ) : null}
              </td>
              {chiDoc ? null : (
                <td className="right">
                  {grant.revocable && canRevoke ? (
                    <button
                      className="btn btn-sm btn-danger"
                      type="button"
                      disabled={revoke.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            t('perm.revoked', {
                              name: grant.user.fullName,
                              domain: domainName,
                            }),
                          )
                        ) {
                          revoke.mutate(grant);
                        }
                      }}
                    >
                      {t('perm.revoke')}
                    </button>
                  ) : (
                    <span className="small muted">{t('perm.notRevocable')}</span>
                  )}
                </td>
              )}
            </tr>
          ))}

          {/* FR-085 — lời mời chưa kích hoạt, không bịa là đã có quyền. */}
          {pending.map((invite) => (
            <tr key={invite.id}>
              <td>
                <div className="row" style={{ gap: 9, flexWrap: 'nowrap' }}>
                  <span className="avatar sm">@</span>
                  <span>
                    <b>{invite.email}</b>
                    <div className="sub">{t('perm.pendingTitle')}</div>
                  </span>
                </div>
              </td>
              <td>
                <span className="chip warn tag-role">{t('perm.pendingChip')}</span>
              </td>
              <td>
                <span className="small muted">{invite.domainRole?.name ?? '—'}</span>
              </td>
              <td className="small muted">{formatDateTime(invite.createdAt)}</td>
              {chiDoc ? null : (
                <td className="right">
                  <div className="row-actions">
                    {onResendInvite && canShare && invite.domainRole ? (
                      <button
                        className="btn btn-sm"
                        type="button"
                        data-od-id="resend-invite"
                        onClick={() => onResendInvite(invite)}
                      >
                        {t('perm.pendingResend')}
                      </button>
                    ) : null}
                    {onRevokeInvite && canRevoke ? (
                      <button
                        className="btn btn-sm btn-danger"
                        type="button"
                        onClick={() => onRevokeInvite(invite.id)}
                      >
                        {t('perm.pendingRevoke')}
                      </button>
                    ) : null}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Tabpanel *Quyền truy cập* của `domain-detail.html` (data-od-id="panel-access")
 * kèm nút *Chia sẻ quyền* (data-od-id="modal-share").
 */
export function DomainAccessPanel({
  domainId,
  domainName,
}: {
  domainId: string;
  domainName: string;
}) {
  const grantsQuery = useQuery({
    queryKey: permKeys.domainGrants(domainId),
    queryFn: () => permApi.domainGrants(domainId),
  });

  /*
   * OQ-243 — tab này CHỈ HIỂN THỊ.
   *
   * Chủ dự án: *"chúng ta đã có chức năng quản trị là phân quyền tên miền rồi
   * thì tab Quyền truy cập đó chỉ nên để hiển thị thông tin về quyền thôi"*.
   *
   * Hai chỗ cùng làm một việc là hai chỗ phải sửa mỗi lần luật đổi, và là hai
   * chỗ có thể lệch nhau. Chính sự trùng lặp ấy sinh ra lỗi anh vừa báo: bảng ở
   * đây kiểm quyền bằng `can('share.grant')` — quyền HỆ THỐNG mà mọi Thành viên
   * đều có — nên người chỉ được cấp "Biên tập DNS" vẫn thấy ô đổi mức quyền,
   * kể cả mức của chính họ. Backend chặn đúng (`bothLayers`), nhưng giao diện
   * nói sai với người dùng về quyền họ đang có.
   *
   * Gỡ chức năng khỏi đây thì lỗi ấy hết đường tái diễn, thay vì phải nhớ đồng
   * bộ điều kiện ở hai nơi.
   */
  return (
    <div className="tabpanel" data-od-id="panel-access">
      <div className="card-body stack">
        <AsyncSection
          loading={grantsQuery.isLoading}
          error={grantsQuery.error}
          data={grantsQuery.data}
          onRetry={() => void grantsQuery.refetch()}
          skeleton={<SkeletonCard rows={4} />}
        >
          {(data) => (
            <DomainAccessTable
              domainId={domainId}
              domainName={domainName}
              chiDoc
              grants={data.items}
              pending={data.pending}
              odId="access-table"
            />
          )}
        </AsyncSection>
      </div>
    </div>
  );
}
